/* eslint-disable */
import { watch } from "vue";
import { handleApiError } from "./error-handler";
import Axios from "./axios-wrapper";
import emitter from "@/mitt";
import { ACCESS_TOKEN_KEY, GlobalEventAction, PARENT_TYPE_DREESUITE, PARENT_TYPE_HEADER } from "@/constant";
import { isAuthenticated } from "./useIframeAuth";
import { isParentAppHost, readApmToken } from "./useParentAppToken";

// Endpoints that must not be hit until the iframe-auth flow has produced a JWT.
// Listed in the user-facing /api/v1/... form for clarity; matched as substrings
// against the outgoing request URL (which is built from VUE_APP_BASEURL + path).
const BLOCKED_PATHS_WHEN_UNAUTH: ReadonlyArray<string> = [
  "/api/v1/neighborhood/",
  "/api/v1/panel/community",
  "/api/v1/legend",
  "/api/v1/s3/",
];

const AUTH_WAIT_TIMEOUT_MS = 30000;

const isBlockedWhileUnauthenticated = (url: string | undefined): boolean => {
  if (!url) return false;
  return BLOCKED_PATHS_WHEN_UNAUTH.some((path) => url.includes(path));
};

// Resolves when `isAuthenticated` flips to true, or rejects after the timeout.
// The Axios request interceptor awaits this for blocked URLs, so requests fired
// before auth completes are queued and replayed automatically once the JWT lands.
const waitUntilAuthenticated = (): Promise<void> =>
  new Promise((resolve, reject) => {
    if (isAuthenticated.value) {
      resolve();
      return;
    }
    const stop = watch(isAuthenticated, (authed) => {
      if (authed) {
        clearTimeout(timer);
        stop();
        resolve();
      }
    });
    const timer = setTimeout(() => {
      stop();
      reject(new Error("Auth wait timed out before request could be sent"));
    }, AUTH_WAIT_TIMEOUT_MS);
  });

const transformAxiosResponse = async (_axios: any) => await _axios.then(async (res: any) => await Promise.resolve(res.data)).catch(async (err: any) => await handleApiError(err.response));

const requestGetDelete = async (method: string, url: string, extras: any = {}) => await transformAxiosResponse(Axios.Client[method](url, extras));

const registerResponseInterceptors = async () => Axios.Client.interceptors.response.use((response) => response, raisePostMessage);
const registerRequestInterceptors = async () => Axios.Client.interceptors.request.use(injectAuthToken,   (error) => Promise.reject(error));

const injectAuthToken = async (request) => {
    if (!isAuthenticated.value && isBlockedWhileUnauthenticated(request.url)) {
      await waitUntilAuthenticated();
    }
    // Host-app deployments carry DreeSuite's APM token, read fresh on every
    // request so a token rotated in place by the host is picked up immediately.
    // `parentType` tells the backend to validate it against the APM validator
    // rather than verifying a broker-exchanged JWT.
    let token: string | null;
    if (isParentAppHost()) {
      token = readApmToken();
      request.headers[PARENT_TYPE_HEADER] = PARENT_TYPE_DREESUITE;
    } else {
      token = localStorage.getItem(ACCESS_TOKEN_KEY);
    }
    if (token) {
      request.headers.Authorization = `Bearer ${token}`;
    }
    return request;
}

const raisePostMessage = (error) => {
    if (error.response?.status === 401) {
      emitter.emit(GlobalEventAction.Unauthorized)
    }
    return Promise.reject(error);
  }
const requestPutPatchPost = async (method: string, url: string, data: any, extras: any = {}) => await transformAxiosResponse(Axios.Client[method](url, data, extras));

const get = async (url: string, extras: any = {}) => await requestGetDelete("get", url, extras);
const del = async (url: string, extras: any = {}) => await requestGetDelete("delete", url, extras);
const put = async (url: string, data: any, extras: any = {}) => await requestPutPatchPost("put", url, data, extras);
const post = async (url: string, data: any, extras: any = {}) => await requestPutPatchPost("post", url, data, extras);
const patch = async (url: string, data: any, extras: any = {}) => await requestPutPatchPost("patch", url, data, extras);

export default { get, put, post, patch, delete: del, registerResponseInterceptors, registerRequestInterceptors };
